/**
 * App component with primary route. Initialize services here
 */

import React from 'react';
import { Route } from 'react-router-dom';
import { IonApp, IonContent, IonPage, IonRouterOutlet, IonSpinner } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';

//import Home from './pages/Home';
import Home from './pages/Home';
import withApplicationState from './store';
import { apiURI, firebaseAnalyticsWebOptions, adMobIds, ADS_OFF } from './ts/environment';
import { setupIonicReact } from '@ionic/react';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';
import './components/LaunchAgeGate.css';

import { FirebaseAnalytics } from '@capacitor-community/firebase-analytics';
import { AdMob, AdmobConsentStatus } from '@capacitor-community/admob';
import { Device } from '@capacitor/device';
import { Preferences } from '@capacitor/preferences';
import '@capacitor-community/firebase-analytics';
import { App } from '@capacitor/app';

import { platformActionTypes, deviceActionTypes, uuidActionTypes, focusedActionTypes } from './ts/actionTypes';
import AdMobFuncs from './ts/adMob';
import axios from 'axios';

import { runGameTester } from './ts/gameTester';
import LaunchAgeGate from './components/LaunchAgeGate';
import AgeGateUnavailable from './pages/AgeGateUnavailable';

setupIonicReact({
  mode: 'md'
});

const APP_NAME = 'Phrasigins';

const getPlatform = async () => {
  const deviceObject = await Device.getInfo();
  const initialPlatform = deviceObject.platform;
  //console.log(platform);
  if ( initialPlatform === 'ios' ) {
    setupIonicReact({
      mode: 'ios'
    });
  } else {
    setupIonicReact({
      mode: 'md'
    });
  } 
  return deviceObject;
}

const logPlatform = getPlatform();
logPlatform.then(value => {
  //console.log('Platform:', value);
});

const applyAdsOffStyles = () => {
  if (!ADS_OFF) {
    return;
  }

  document.body.classList.add('ads-off');

  if (document.getElementById('ads-off-overrides')) {
    return;
  }

  const styleElement = document.createElement('style');
  styleElement.id = 'ads-off-overrides';
  styleElement.innerHTML = `
    .ads-off .ad-buffer { height: 0 !important; padding-top: 0 !important; }
    .ios .ads-off .ad-buffer { margin-top: env(safe-area-inset-top) !important; }
  `;
  document.head.appendChild(styleElement);
};

interface AppProps {
  dispatch: ({ type }: { type: string; payload?: any; }) => void;
};

interface AppState {
  bootstrapComplete: boolean;
  isInitializingApp: boolean;
  servicesInitialized: boolean;
  platform: string;
  uuid: string;
  osVersion: string;
  ageGateBlockedMessage: string;
};


class Wordigins extends React.Component<AppProps, AppState> {
  constructor(props: AppProps) {
    super(props);

    this.state = {
      bootstrapComplete: false,
      isInitializingApp: false,
      servicesInitialized: false,
      platform: '',
      uuid: '',
      osVersion: '',
      ageGateBlockedMessage: '',
    };
  }

  /**
   * Avoid changing structure of this function, some things need to be done in order.
   * ================================================================================
   * */
  async componentDidMount() {
    const { platform, model, osVersion } = (await Device.getInfo());
    const { identifier } = (await Device.getId());
    const uuid = identifier;

    applyAdsOffStyles();

    await Preferences.migrate();

    this.props.dispatch({
      type: platformActionTypes.SET_PLATFORM,
      payload: platform
    });

    this.props.dispatch({
      type: deviceActionTypes.SET_DEVICE,
      payload: model
    });

    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.props.dispatch({
          type: focusedActionTypes.SET_FOCUSED
        });
      }
    });

    this.props.dispatch({
      type: uuidActionTypes.SET_UUID,
      payload: uuid
    });

    this.setState({
      bootstrapComplete: true,
      platform,
      uuid,
      osVersion,
    });

    if (platform === 'web') {
      await this.initializeApp(platform);
    }
  }

  async initializeApp(platformOverride?: string) {
    const platform = platformOverride ?? this.state.platform;

    if (this.state.servicesInitialized || this.state.isInitializingApp) {
      console.log('[AgeGate] app initialization request ignored because services are already running or ready', {
        platform,
        servicesInitialized: this.state.servicesInitialized,
        isInitializingApp: this.state.isInitializingApp,
      });
      return;
    }

    console.log('[AgeGate] starting app initialization after launch checks', {
      platform,
    });

    this.setState({
      isInitializingApp: true,
    });

    try {
      if (platform === 'web') {
        await FirebaseAnalytics.initializeFirebase(firebaseAnalyticsWebOptions);
      } else if (!ADS_OFF) {
        const platformAdId = (platform === 'ios') ? adMobIds.iosBanner : adMobIds.androidBanner;

        AdMob.initialize()
        .then(() => console.log('AdMob initialized successfully'))
        .catch((error) => console.error('AdMob initialization failed:', error));

        try {
          const consentInfo = await AdMob.requestConsentInfo();
          console.log('AdMob consent info:', consentInfo);

          if (consentInfo.isConsentFormAvailable && consentInfo.status === AdmobConsentStatus.REQUIRED) {
            const { status } = await AdMob.showConsentForm();
            console.log('AdMob consent form status:', status);
          }
        } catch (error) {
          console.warn('AdMob consent info request failed; proceeding to show banner anyway:', error);
        }

        console.log('admob Using Ad Unit ID:', platformAdId);
        AdMobFuncs.showBanner(platformAdId);
        AdMobFuncs.hideBanner();
      }

      FirebaseAnalytics.setCollectionEnabled({enabled: true});

      try {
        await axios.post(apiURI + '/user/register', {}, {});
      } catch (error) {
        console.log('User Registration Failed');
        console.error(error);
      }

      runGameTester();

      this.setState({
        servicesInitialized: true,
      });

      console.log('[AgeGate] app initialization completed', {
        platform,
      });
    } finally {
      this.setState({
        isInitializingApp: false,
      });
    }
  }

  async handleAgeGateAllow() {
    console.log('[AgeGate] launch checks passed, continuing into app initialization', {
      platform: this.state.platform,
    });

    this.setState({
      ageGateBlockedMessage: '',
    });

    await this.initializeApp();
  }

  handleAgeGateBlock(message: string) {
    console.warn('[AgeGate] showing unavailable screen', {
      platform: this.state.platform,
      message,
    });

    this.setState({
      ageGateBlockedMessage: message,
    });
  }

  renderLaunchLoadingScreen () {
    return (
      <IonPage>
        <IonContent fullscreen={true} className="launch-age-gate-content">
          <div className="launch-age-gate-shell">
            <div className="launch-age-gate-loading-shell">
              <div className="launch-age-gate-loading">
                <div className="launch-age-gate-loading-primary">
                  <IonSpinner name="crescent" />
                  <span>Preparing {APP_NAME}...</span>
                </div>
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  render () {
    const { bootstrapComplete, isInitializingApp, servicesInitialized, platform, uuid, osVersion, ageGateBlockedMessage } = this.state;

    if (!bootstrapComplete || isInitializingApp) {
      return (
        <IonApp>
          {this.renderLaunchLoadingScreen()}
        </IonApp>
      );
    }

    if (!servicesInitialized && platform !== 'web') {
      if (ageGateBlockedMessage) {
        return (
          <IonApp>
            <AgeGateUnavailable
              appName={APP_NAME}
              message={ageGateBlockedMessage}
              platform={platform}
            />
          </IonApp>
        );
      }

      return (
        <IonApp>
          <LaunchAgeGate
            appName={APP_NAME}
            platform={platform}
            uuid={uuid}
            osVersion={osVersion}
            onAllow={this.handleAgeGateAllow.bind(this)}
            onBlock={this.handleAgeGateBlock.bind(this)}
            autoRunRealCheck={platform !== 'web'}
          />
        </IonApp>
      );
    }

    return (
      <IonApp>
        <IonReactRouter>
          <IonRouterOutlet>
            <Route path="/" component={Home} />
          </IonRouterOutlet>
        </IonReactRouter>
      </IonApp>
    );
  }
};

export default withApplicationState(Wordigins);
